import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MagnifyingGlass, MapPin, Bus as BusSimple, Globe, Funnel,
  CaretLeft, CaretRight, ArrowsClockwise, PencilSimple, X,
} from "@phosphor-icons/react";

const REGION_TINTS = {
  intrastate: { dot: "#22C55E", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  interstate: { dot: "#F59E0B", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  unknown:    { dot: "#94A3B8", chip: "bg-slate-50  text-slate-600  border-slate-200"  },
};

// Tolerant getter so the UI stays consistent across pre-import (legacy
// `bus_stop_*`) and post-import (`stop_*`) docs.
const pick = (s, ...keys) => {
  for (const k of keys) if (s?.[k] !== undefined && s?.[k] !== null && s?.[k] !== "") return s[k];
  return null;
};

export default function BusStops() {
  const [stops, setStops] = useState([]);
  const [counts, setCounts] = useState({ intrastate: 0, interstate: 0, total: 0 });
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("all");          // all | intrastate | interstate
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);         // currently-edited stop doc
  const pageSize = 60;

  const fetchCounts = async () => {
    try {
      const { data } = await api.get("/bus-stops/regions");
      setCounts(data);
    } catch { /* keep zeros */ }
  };

  const load = async (query = "", reg = region) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "1000");
      if (query) params.set("q", query);
      if (reg !== "all") params.set("region", reg);
      const { data } = await api.get(`/bus-stops?${params.toString()}`);
      setStops(data);
      setPage(0);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchCounts(); load(); }, []);                       // eslint-disable-line
  useEffect(() => { const t = setTimeout(() => load(q, region), 250); return () => clearTimeout(t); }, [q, region]); // eslint-disable-line

  const visible = useMemo(() => stops.slice(page * pageSize, (page + 1) * pageSize), [stops, page]);
  const totalPages = Math.max(1, Math.ceil(stops.length / pageSize));

  const handleSaved = (updated) => {
    // Merge the server response back into the table.
    setStops((prev) => prev.map((s) => s.id === updated.id ? { ...s, ...updated } : s));
    // Re-fetch region counts in case the region was changed.
    fetchCounts();
  };

  return (
    <div className="p-4 lg:p-6 space-y-5 lg:space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-slate-500">Network Registry</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">Bus Stops</h1>
          <p className="text-[12px] text-slate-500 mt-1">
            All {counts.total.toLocaleString("en-IN")} KTCL stops from the official
            <code className="font-mono mx-1">LON LAT EV ANNOUNC</code> register.
            Click the pencil to edit any stop.
          </p>
        </div>
        <button onClick={() => { fetchCounts(); load(q, region); }}
          data-testid="busstops-refresh"
          className="px-3 py-2 rounded-md text-[12px] font-bold bg-slate-100 hover:bg-slate-200 inline-flex items-center gap-1.5">
          <ArrowsClockwise size={13} weight="bold"/> Refresh
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI l="Total Stops"   v={counts.total}      c="#1E3A8A" i={MapPin} testid="busstops-kpi-total"/>
        <KPI l="Intrastate"    v={counts.intrastate} c="#22C55E" i={BusSimple} testid="busstops-kpi-intra"/>
        <KPI l="Interstate"    v={counts.interstate} c="#F59E0B" i={Globe} testid="busstops-kpi-inter"/>
        <KPI l="Showing"       v={stops.length}      c="#8B5CF6" i={Funnel} testid="busstops-kpi-shown"/>
      </div>

      {/* Filter bar */}
      <div className="ktcl-card p-3 lg:p-4 flex flex-wrap items-center gap-2 lg:gap-3" data-testid="busstops-filters">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-md w-full sm:w-96">
          <MagnifyingGlass size={14} className="text-slate-500"/>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name · code · stop_id · stage…"
            data-testid="busstops-search"
            className="bg-transparent text-sm outline-none flex-1 min-w-0"/>
        </div>
        <div className="flex items-center gap-1.5" data-testid="busstops-region-tabs">
          {[
            { v: "all",        l: "All",         n: counts.total },
            { v: "intrastate", l: "Intrastate",  n: counts.intrastate },
            { v: "interstate", l: "Interstate",  n: counts.interstate },
          ].map((t) => (
            <button key={t.v} onClick={() => setRegion(t.v)}
              data-testid={`busstops-region-${t.v}`}
              className={`px-3 py-1.5 rounded-md text-[12px] font-bold border transition-colors
                          ${region === t.v
                            ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {t.l} <span className="tabular-nums opacity-75">· {t.n.toLocaleString("en-IN")}</span>
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-slate-500" data-testid="busstops-count-line">
          {loading ? "Loading…" : `Page ${page + 1} of ${totalPages} · showing ${visible.length} of ${stops.length}`}
        </div>
      </div>

      {/* Stop table — Routes + ETM Short columns removed per owner request */}
      <div className="ktcl-card overflow-hidden">
        <div className="overflow-x-auto scroll-thin max-h-[calc(100vh-340px)]">
          <table className="w-full text-sm min-w-[900px]" data-testid="busstops-table">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 font-bold">Stop ID</th>
                <th className="px-3 py-2 font-bold">Stop Name</th>
                <th className="px-3 py-2 font-bold">Code</th>
                <th className="px-3 py-2 font-bold">Stage</th>
                <th className="px-3 py-2 font-bold">Region</th>
                <th className="px-3 py-2 font-bold text-right">Onward (lat,lng)</th>
                <th className="px-3 py-2 font-bold text-right">Return (lat,lng)</th>
                <th className="px-3 py-2 font-bold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => {
                const id   = pick(s, "stop_id", "bus_stop_id") ?? "—";
                const name = pick(s, "stop_name", "bus_stop_name", "name") || "(unnamed)";
                const code = pick(s, "short_code", "bus_stop_code", "code") || "—";
                const stage = pick(s, "stage_name") || "";
                const reg  = pick(s, "region") || "unknown";
                const oLat = pick(s, "onward_lat", "lat");
                const oLng = pick(s, "onward_lng", "lng");
                const rLat = pick(s, "return_lat");
                const rLng = pick(s, "return_lng");
                const tint = REGION_TINTS[reg] || REGION_TINTS.unknown;
                return (
                  <tr key={s.id || `${id}-${code}`}
                       className="border-t border-slate-100 hover:bg-slate-50 transition-colors"
                       data-testid={`busstops-row-${id}`}>
                    <td className="px-3 py-2 font-mono text-[12px] tabular-nums">#{id}</td>
                    <td className="px-3 py-2 font-semibold">{name}</td>
                    <td className="px-3 py-2">
                      <span className="font-mono text-[11px] font-bold px-2 py-0.5 bg-blue-50 text-[#1E3A8A] rounded">{code}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-[12px]">{stage}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-0.5 rounded border ${tint.chip}`}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: tint.dot }}/>
                        {reg}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[10.5px] text-slate-500 tabular-nums">
                      {oLat != null && oLng != null
                        ? `${Number(oLat).toFixed(5)}, ${Number(oLng).toFixed(5)}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[10.5px] text-slate-500 tabular-nums">
                      {rLat != null && rLng != null
                        ? `${Number(rLat).toFixed(5)}, ${Number(rLng).toFixed(5)}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setEditing(s)}
                        data-testid={`busstops-edit-${id}`}
                        title="Edit stop"
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-[#1E3A8A]/20 rounded text-[#1E3A8A] bg-blue-50 hover:bg-blue-100 font-bold">
                        <PencilSimple size={12} weight="bold"/> Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="px-3 py-12 text-center text-sm text-slate-500"
                       data-testid="busstops-empty">
                    No stops match the current filters.
                    {region !== "all" && (
                      <> Try <button onClick={() => setRegion("all")}
                           className="underline font-bold text-[#1E3A8A]">All regions</button>.</>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-slate-100 bg-slate-50">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              data-testid="busstops-prev"
              className="px-2 py-1 rounded border border-slate-200 text-xs disabled:opacity-40">
              <CaretLeft size={12}/>
            </button>
            <span className="text-xs tabular-nums text-slate-600" data-testid="busstops-pageinfo">
              {page + 1} / {totalPages}
            </span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              data-testid="busstops-next"
              className="px-2 py-1 rounded border border-slate-200 text-xs disabled:opacity-40">
              <CaretRight size={12}/>
            </button>
          </div>
        )}
      </div>

      {/* Inline-edit modal */}
      {editing && (
        <EditStopModal
          stop={editing}
          onClose={() => setEditing(null)}
          onSaved={(u) => { handleSaved(u); setEditing(null); }}
        />
      )}
    </div>
  );
}

const KPI = ({ l, v, c, i: Icon, testid }) => (
  <div className="ktcl-card ktcl-card-hover p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="label-cap text-slate-500">{l}</div>
      <Icon size={18} weight="duotone" style={{ color: c }}/>
    </div>
    <div className="font-display text-2xl font-bold mt-2 tabular-nums" style={{ color: c }}>
      {(v ?? 0).toLocaleString("en-IN")}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Inline edit modal — same page, no navigation.
// Posts to PUT /bus-stops/{id}; backend mirrors writes between the new + legacy
// field names so any consumer reading either name keeps working.
// ---------------------------------------------------------------------------
function EditStopModal({ stop, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    stop_name:  pick(stop, "stop_name", "bus_stop_name", "name") || "",
    short_code: pick(stop, "short_code", "bus_stop_code", "code") || "",
    stage_name: pick(stop, "stage_name") || "",
    region:     pick(stop, "region") || "intrastate",
    onward_lat: pick(stop, "onward_lat", "lat") ?? "",
    onward_lng: pick(stop, "onward_lng", "lng") ?? "",
    return_lat: pick(stop, "return_lat") ?? "",
    return_lng: pick(stop, "return_lng") ?? "",
    depot:      pick(stop, "depot") || "",
    notes:      pick(stop, "notes") || "",
  }));
  const [saving, setSaving] = useState(false);

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !saving) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saving, onClose]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.stop_name.trim()) { toast.error("Stop name is required"); return; }
    if (!form.short_code.trim()) { toast.error("Short code is required"); return; }
    if (!["intrastate", "interstate"].includes(form.region)) {
      toast.error("Region must be Intrastate or Interstate"); return;
    }
    setSaving(true);
    const payload = {
      stop_name:  form.stop_name.trim(),
      short_code: form.short_code.trim().toUpperCase(),
      stage_name: form.stage_name.trim().toUpperCase(),
      region:     form.region,
      depot:      form.depot.trim(),
      notes:      form.notes.trim(),
    };
    // Numeric coords — send only when populated and parseable.
    for (const k of ["onward_lat", "onward_lng", "return_lat", "return_lng"]) {
      if (form[k] !== "" && form[k] !== null) {
        const n = Number(form[k]);
        if (!Number.isNaN(n)) payload[k] = n;
      }
    }
    try {
      const { data } = await api.put(`/bus-stops/${stop.id}`, payload);
      toast.success(`Updated ${data.stop_name || data.bus_stop_name}`);
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const headerName = pick(stop, "stop_name", "bus_stop_name") || "(unnamed stop)";
  const headerId   = pick(stop, "stop_id", "bus_stop_id") ?? "—";

  return (
    <div className="fixed inset-0 bg-black/50 z-[1400] flex items-center justify-center p-4"
         data-testid="busstops-edit-dialog"
         onClick={() => { if (!saving) onClose(); }}>
      <div className="bg-white rounded-md max-w-2xl w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="label-cap text-slate-500">Bus Stop · #{headerId}</div>
            <h2 className="font-display font-bold text-lg truncate">{headerName}</h2>
          </div>
          <button onClick={onClose} disabled={saving}
            data-testid="busstops-edit-close"
            className="w-8 h-8 rounded-md bg-slate-100 text-slate-500 inline-flex items-center justify-center disabled:opacity-40"
            aria-label="Close"><X size={16}/></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Fld label="Stop name *">
            <In v={form.stop_name} on={(v) => set("stop_name", v)}
                testid="busstops-edit-name" placeholder="e.g. Margao KTC Bus Stand"/>
          </Fld>
          <Fld label="Short code (3-letter) *">
            <In v={form.short_code} on={(v) => set("short_code", v.toUpperCase())}
                testid="busstops-edit-code" placeholder="MRG" maxLength={6}/>
          </Fld>
          <Fld label="Stage name">
            <In v={form.stage_name} on={(v) => set("stage_name", v.toUpperCase())}
                testid="busstops-edit-stage" placeholder="MARGAO"/>
          </Fld>
          <Fld label="Region *">
            <select value={form.region} onChange={(e) => set("region", e.target.value)}
              data-testid="busstops-edit-region"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]">
              <option value="intrastate">Intrastate (Goa)</option>
              <option value="interstate">Interstate</option>
            </select>
          </Fld>
          <Fld label="Onward latitude">
            <In v={form.onward_lat} on={(v) => set("onward_lat", v)}
                testid="busstops-edit-olat" placeholder="15.28785"/>
          </Fld>
          <Fld label="Onward longitude">
            <In v={form.onward_lng} on={(v) => set("onward_lng", v)}
                testid="busstops-edit-olng" placeholder="73.95536"/>
          </Fld>
          <Fld label="Return latitude">
            <In v={form.return_lat} on={(v) => set("return_lat", v)}
                testid="busstops-edit-rlat" placeholder="15.28775"/>
          </Fld>
          <Fld label="Return longitude">
            <In v={form.return_lng} on={(v) => set("return_lng", v)}
                testid="busstops-edit-rlng" placeholder="73.95552"/>
          </Fld>
          <Fld label="Depot (optional)">
            <In v={form.depot} on={(v) => set("depot", v)}
                testid="busstops-edit-depot" placeholder="MRG"/>
          </Fld>
          <Fld label="Notes">
            <In v={form.notes} on={(v) => set("notes", v)}
                testid="busstops-edit-notes" placeholder="Optional remarks"/>
          </Fld>
        </div>

        <div className="flex gap-2 pt-2 sticky bottom-0 bg-white">
          <button onClick={onClose} disabled={saving}
            data-testid="busstops-edit-cancel"
            className="flex-1 px-3 py-2 rounded-md text-sm border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            data-testid="busstops-edit-save"
            className="ktcl-btn-primary flex-1 px-3 py-2 rounded-md text-sm font-bold disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Fld = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{label}</div>
    {children}
  </div>
);
const In = ({ v, on, testid, placeholder, maxLength }) => (
  <input value={v} onChange={(e) => on(e.target.value)}
    data-testid={testid} placeholder={placeholder} maxLength={maxLength}
    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
);
